// src/pages/ProductsPage.js — the Item List  [marker: item-list v1]
// Everything that can go on an invoice: stand-alone items plus the tickets and
// sponsorships of every event (created and kept in sync automatically).
// List, filter, create, edit, image upload, delete.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import DataGrid from '../components/DataGrid';
import { AccountSelect } from '../components/AccountSelect'; // [marker: coa v1]

const CURRENCY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export default function ProductsPage() {
  // [marker: step1-templates-grids v1] Excel-style Item List (everything loads; filter in the grid)
  const { isManagerOrAbove } = useAuth();
  const location = useLocation();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('true'); // 'true' | 'false' | 'all'
  const [modalState, setModalState] = useState({ open: false, mode: 'add', product: null });
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/invoice-items?is_active=all');
      setProducts(res.data || []);
    } catch (err) {
      toast.error('Failed to load items.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/invoice-items/categories');
      setCategories(res.data);
    } catch (err) { /* non-fatal */ }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  // [marker: invoice-links v1] /products?item=ID opens that item (links from invoices)
  useEffect(() => {
    const want = new URLSearchParams(location.search).get('item');
    if (!want || !products.length) return;
    const p = products.find(x => String(x.id) === String(want));
    if (p) setModalState({ open: true, mode: 'edit', product: p });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.length, location.search]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openAdd  = () => setModalState({ open: true, mode: 'add',  product: null });
  const openEdit = (product) => setModalState({ open: true, mode: 'edit', product });
  const closeModal = () => setModalState({ open: false, mode: 'add', product: null });
  const onSavedOrDeleted = () => { closeModal(); fetchProducts(); fetchCategories(); };

  const shown = products.filter(p =>
    activeFilter === 'all' ? true : activeFilter === 'true' ? p.is_active !== false : p.is_active === false
  );
  const typeLabel = p => (p.source === 'ticket' ? 'Ticket' : p.source === 'sponsorship' ? 'Sponsorship' : 'Item');

  const columns = [
    { key: 'image', label: '', width: 44, text: () => '',
      render: p => p.image_url
        ? <img src={p.image_url} alt="" style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 3, verticalAlign: 'middle' }} />
        : null },
    { key: 'name', label: 'Name', width: 260, render: p => <strong>{p.name}</strong> },
    { key: 'description', label: 'Description', width: 220 },
    { key: 'type', label: 'Type', width: 110, get: typeLabel,
      render: p => p.source === 'ticket' ? <span className="badge badge-info">Ticket</span>
        : p.source === 'sponsorship' ? <span className="badge badge-warning">Sponsorship</span>
        : <span className="badge badge-muted">Item</span> },
    { key: 'linked', label: 'Linked To', width: 170, defaultHidden: false,
      get: p => p.link_tier_name ? `Sponsorship: ${p.link_tier_name}` : p.link_ticket_type_name ? `Ticket: ${p.link_ticket_type_name}` : '' },
    { key: 'event_name', label: 'Event', width: 200,
      render: p => p.event_id
        ? <Link to={`/events/${p.event_id}`} onClick={e => e.stopPropagation()}>{p.event_name || 'Event'}</Link>
        : null },
    { key: 'event_date', label: 'Event Date', width: 105, type: 'date' },
    { key: 'category', label: 'Category', width: 130 },
    { key: 'sku', label: 'SKU', width: 100, defaultHidden: true },
    { key: 'default_price', label: 'Price', width: 100, type: 'money', align: 'right' },
    // [marker: coa v1] which income account the item's sales go to
    { key: 'income_account', label: 'Income Account', width: 220,
      get: p => (p.income_account_name ? (p.income_account_parent ? `${p.income_account_parent}:${p.income_account_name}` : p.income_account_name) : ''),
      render: p => p.income_account_name
        ? <span title={p.income_account_parent ? `${p.income_account_parent}:${p.income_account_name}` : p.income_account_name}>{p.income_account_name}</span>
        : <span style={{ color: 'var(--danger)', fontSize: '0.8rem' }}>⚠ not set</span> },
    { key: 'times_invoiced', label: 'On Invoices', width: 95, type: 'number' },
    { key: 'tickets_sold', label: 'Tickets Sold', width: 100, type: 'number' },
    { key: 'sponsors_count', label: 'Sponsors', width: 85, type: 'number' },
    { key: 'inventory', label: 'In Stock', width: 90, type: 'number',
      get: p => (p.inventory_count === null || p.inventory_count === undefined ? null : p.inventory_count - (p.inventory_sold || 0)),
      defaultHidden: true },
    { key: 'member_only', label: 'Members Only', width: 105, type: 'bool' },
    { key: 'is_recurring', label: 'Recurring', width: 90, type: 'bool', defaultHidden: true },
    { key: 'is_active', label: 'Active', width: 75, type: 'bool' },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Item List</h1>
          <p className="page-subtitle">
            {loading ? '—' : `${shown.length} item${shown.length === 1 ? '' : 's'}`}
            {!loading && shown.some(p => !p.income_account_id) && (
              <span style={{ color: 'var(--danger)' }}>{' · '}{shown.filter(p => !p.income_account_id).length} without an income account</span>
            )}
            {' · '}Event tickets and sponsorships are added here automatically.
          </p>
        </div>
        {isManagerOrAbove() && (
          <button className="btn btn-primary" onClick={openAdd}>+ Add Item</button>
        )}
      </div>

      <DataGrid
        gridKey="item-list"
        rows={shown}
        loading={loading}
        columns={columns}
        onRowClick={openEdit}
        emptyText="No items match."
        selectable={isManagerOrAbove()}
        selectedIds={selected}
        onSelectionChange={setSelected}
        toolbarRight={selected.length > 0 && (
          <button className="btn btn-primary btn-sm" onClick={() => setBulkOpen(true)}>
            Set income account ({selected.length})
          </button>
        )}
        toolbarLeft={
          <select className="form-select" style={{ width: 'auto', height: 32 }} value={activeFilter} onChange={e => setActiveFilter(e.target.value)}>
            <option value="true">Active items</option>
            <option value="false">Inactive items</option>
            <option value="all">All items</option>
          </select>
        }
      />

      {bulkOpen && (
        <BulkIncomeAccountModal
          ids={selected}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); setSelected([]); fetchProducts(); }}
        />
      )}

      {modalState.open && (
        <ProductModal
          mode={modalState.mode}
          product={modalState.product}
          existingCategories={categories}
          canEdit={isManagerOrAbove()}
          onClose={closeModal}
          onSaved={onSavedOrDeleted}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// ProductModal
// ──────────────────────────────────────────────────────────────────────────────
function ProductModal({ mode, product, existingCategories, canEdit, onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const fileInputRef = useRef(null);
  // Items that come from an event are kept in sync from the event page
  const fromEvent = !!(isEdit && (product?.ticket_type_id || product?.sponsorship_tier_id));

  const [form, setForm] = useState({
    name:           product?.name           ?? '',
    sku:            product?.sku            ?? '',
    category:       product?.category       ?? '',
    description:    product?.description    ?? '',
    defaultPrice:   product?.default_price  ?? '',
    inventoryCount: product?.inventory_count ?? '',  // '' = unlimited
    memberOnly:     product?.member_only    ?? false,
    isRecurring:    product?.is_recurring   ?? false,
    isActive:       product?.is_active      ?? true,
    incomeAccountId: product?.income_account_id ?? null,
  });
  const [link, setLink] = useState({
    eventId: product?.event_id || null,
    tierId: product?.link_tier_id || null,
    ticketTypeId: product?.link_ticket_type_id || null,
  });

  const [imageUrl, setImageUrl] = useState(product?.image_url || null);
  const [saving, setSaving]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Product name is required.');
      return;
    }
    if (form.defaultPrice === '' || isNaN(parseFloat(form.defaultPrice))) {
      toast.error('Price must be a number.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name:           form.name.trim(),
        description:    form.description.trim() || null,
        defaultPrice:   parseFloat(form.defaultPrice),
        sku:            form.sku.trim() || null,
        category:       form.category.trim() || null,
        inventoryCount: form.inventoryCount === '' ? null : parseInt(form.inventoryCount, 10),
        memberOnly:     !!form.memberOnly,
        isRecurring:    !!form.isRecurring,
        incomeAccountId: form.incomeAccountId || null,
      };
      if (!fromEvent) Object.assign(payload, { eventId: link.eventId || null, linkTierId: link.tierId || null, linkTicketTypeId: link.ticketTypeId || null });
      if (isEdit) payload.isActive = !!form.isActive;

      let savedProduct;
      if (isEdit) {
        const res = await api.patch(`/invoice-items/${product.id}`, payload);
        savedProduct = res.data;
      } else {
        const res = await api.post('/invoice-items', payload);
        savedProduct = res.data;
      }

      // Upload any pending image AFTER the product exists
      if (fileInputRef.current?.files?.[0]) {
        await uploadImage(savedProduct.id, fileInputRef.current.files[0]);
      }

      toast.success(isEdit ? 'Product updated.' : 'Product created.');
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save product.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (productId, file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post(`/invoice-items/${productId}/image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImageUrl(res.data.image_url);
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.error || 'Image upload failed.';
      toast.error(msg);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB.');
      return;
    }
    // For existing products, upload immediately. For new products, hold until save.
    if (isEdit) {
      try { await uploadImage(product.id, file); toast.success('Image uploaded.'); }
      catch (_) { /* error already shown */ }
    } else {
      // Show local preview until save
      const reader = new FileReader();
      reader.onload = (ev) => setImageUrl(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = async () => {
    if (!isEdit || !imageUrl) {
      setImageUrl(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    try {
      await api.delete(`/invoice-items/${product.id}/image`);
      setImageUrl(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.success('Image removed.');
    } catch (err) {
      toast.error('Failed to remove image.');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await api.delete(`/invoice-items/${product.id}`);
      toast.success(res.data.message || 'Product deleted.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete.');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Item' : 'New Item'}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {fromEvent && (
            <div style={{ fontSize: 13, background: 'var(--info-bg, #eff6ff)', border: '1px solid #bfdbfe', borderRadius: 6, padding: '8px 10px' }}>
              This {product.ticket_type_id ? 'ticket' : 'sponsorship'} comes from the event
              {' '}<Link to={`/events/${product.event_id}`} onClick={onClose}><strong>{product.event_name || 'event'}</strong></Link>.
              Its name, price and description are managed on the event page and update here automatically.
            </div>
          )}
          {/* What this item is tied to — [marker: invoice-links v1] */}
          {!fromEvent && canEdit && (
            <ItemLinkFields value={link} onChange={setLink} />
          )}
          {isEdit && <ItemUsage itemId={product.id} onNavigate={onClose} />}

          {/* Image */}
          <div>
            <label className="form-label">Image</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {imageUrl ? (
                <img src={imageUrl} alt="" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e5e5' }} />
              ) : (
                <div style={{ width: 96, height: 96, background: '#f5f5f5', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 12 }}>
                  No image
                </div>
              )}
              {canEdit && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                  <button type="button" className="btn btn-secondary btn-sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}>
                    {uploading ? 'Uploading…' : (imageUrl ? 'Replace' : 'Upload Image')}
                  </button>
                  {imageUrl && (
                    <button type="button" className="btn btn-ghost btn-sm"
                            onClick={handleRemoveImage}
                            disabled={uploading}>
                      Remove
                    </button>
                  )}
                  <small style={{ color: '#888' }}>JPG, PNG, WebP, GIF — max 5 MB</small>
                </div>
              )}
            </div>
          </div>

          {/* Name + SKU */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Name *</label>
              <input className="form-input" value={form.name}
                     onChange={e => setField('name', e.target.value)}
                     disabled={!canEdit || fromEvent} placeholder="e.g. Journal ad – full page" />
            </div>
            <div>
              <label className="form-label">SKU</label>
              <input className="form-input" value={form.sku}
                     onChange={e => setField('sku', e.target.value)}
                     disabled={!canEdit} placeholder="SPONS-GOLD" />
            </div>
          </div>

          {/* Category + Price */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Category</label>
              <input className="form-input" list="product-categories"
                     value={form.category}
                     onChange={e => setField('category', e.target.value)}
                     disabled={!canEdit}
                     placeholder="Memberships, Events, Sponsorships..." />
              <datalist id="product-categories">
                {existingCategories.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <label className="form-label">Default Price *</label>
              <input className="form-input" type="number" step="0.01" min="0"
                     value={form.defaultPrice}
                     onChange={e => setField('defaultPrice', e.target.value)}
                     disabled={!canEdit || fromEvent} placeholder="0.00" />
            </div>
          </div>

          {/* Income account — [marker: coa v1] */}
          <div>
            <label className="form-label">Income Account</label>
            <AccountSelect types={['income', 'other_income']} value={form.incomeAccountId}
                           onChange={v => setField('incomeAccountId', v)} disabled={!canEdit}
                           placeholder="— Choose where this item's sales are recorded —" />
            <small style={{ display: 'block', color: '#888', marginTop: 4 }}>
              Sales of this item post to this account (like QuickBooks). Add or rename accounts under Bookkeeping → Chart of Accounts.
              {fromEvent && ' New tickets/sponsorships of this event pick up the same account automatically.'}
            </small>
          </div>

          {/* Description */}
          <div>
            <label className="form-label">Description</label>
            <textarea className="form-input" rows={3}
                      value={form.description}
                      onChange={e => setField('description', e.target.value)}
                      disabled={!canEdit || fromEvent}
                      placeholder="Optional description shown to staff and members." />
          </div>

          {/* Inventory */}
          <div>
            <label className="form-label">
              Inventory <small style={{ fontWeight: 400, color: '#888' }}>(leave blank for unlimited)</small>
            </label>
            <input className="form-input" type="number" min="0" style={{ maxWidth: 180 }}
                   value={form.inventoryCount}
                   onChange={e => setField('inventoryCount', e.target.value)}
                   disabled={!canEdit}
                   placeholder="Unlimited" />
            {isEdit && form.inventoryCount !== '' && (
              <small style={{ display: 'block', color: '#888', marginTop: 4 }}>
                {product?.inventory_sold || 0} sold so far.
              </small>
            )}
          </div>

          {/* Flags */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: canEdit ? 'pointer' : 'default' }}>
              <input type="checkbox" checked={form.memberOnly}
                     onChange={e => setField('memberOnly', e.target.checked)}
                     disabled={!canEdit} />
              <span><strong>Members only</strong> — hide from public listings</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: canEdit ? 'pointer' : 'default' }}>
              <input type="checkbox" checked={form.isRecurring}
                     onChange={e => setField('isRecurring', e.target.checked)}
                     disabled={!canEdit} />
              <span><strong>Renews annually</strong> — informational flag (auto-renewal cron pending)</span>
            </label>
            {isEdit && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: canEdit ? 'pointer' : 'default' }}>
                <input type="checkbox" checked={form.isActive}
                       onChange={e => setField('isActive', e.target.checked)}
                       disabled={!canEdit} />
                <span><strong>Active</strong> — uncheck to hide from product pickers</span>
              </label>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {isEdit && canEdit && !fromEvent ? (
            confirmDelete ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ color: '#c00', fontSize: 13 }}>Really delete?</span>
                <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting}>
                  {deleting ? 'Deleting…' : 'Yes, delete'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(false)}>Cancel</button>
              </div>
            ) : (
              <button className="btn btn-danger" onClick={() => setConfirmDelete(true)}>Delete</button>
            )
          ) : <span />}

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            {canEdit && (
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || uploading}>
                {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create Item')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// BulkIncomeAccountModal — [marker: coa v1]
// ──────────────────────────────────────────────────────────────────────────────
function BulkIncomeAccountModal({ ids, onClose, onDone }) {
  const [accountId, setAccountId] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!accountId) return toast.error('Choose an income account.');
    setSaving(true);
    try {
      const r = await api.post('/invoice-items/bulk-income-account', { ids, incomeAccountId: accountId });
      toast.success(`Updated ${r.data.updated} item${r.data.updated === 1 ? '' : 's'}.`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not update the items.');
    } finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Set Income Account</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ marginTop: 0, fontSize: '0.875rem' }}>Post sales of the <strong>{ids.length}</strong> selected item{ids.length === 1 ? '' : 's'} to:</p>
          <AccountSelect types={['income', 'other_income']} value={accountId} onChange={setAccountId} />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Apply'}</button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// ItemLinkFields / ItemUsage — [marker: invoice-links v1]
// ──────────────────────────────────────────────────────────────────────────────
function ItemLinkFields({ value, onChange }) {
  const [events, setEvents] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [types, setTypes] = useState([]);
  useEffect(() => { api.get('/events?limit=5000').then(r => setEvents(r.data?.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!value.eventId) { setTiers([]); setTypes([]); return; }
    api.get(`/events/${value.eventId}/sponsorship-tiers`).then(r => setTiers(r.data || [])).catch(() => setTiers([]));
    api.get(`/events/${value.eventId}/ticket-types`).then(r => setTypes(r.data?.data || r.data || [])).catch(() => setTypes([]));
  }, [value.eventId]);
  const kindVal = value.tierId ? `t:${value.tierId}` : value.ticketTypeId ? `k:${value.ticketTypeId}` : '';
  return (
    <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: '10px 12px' }}>
      <label className="form-label" style={{ marginBottom: 6 }}>Linked To</label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <select className="form-select" value={value.eventId || ''} onChange={e => onChange({ eventId: e.target.value ? parseInt(e.target.value, 10) : null, tierId: null, ticketTypeId: null })}>
          <option value="">— Not tied to an event —</option>
          {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}{ev.event_date ? ` (${String(ev.event_date).slice(0, 10)})` : ''}</option>)}
        </select>
        <select className="form-select" value={kindVal} disabled={!value.eventId}
                onChange={e => {
                  const v = e.target.value;
                  onChange({ ...value, tierId: v.startsWith('t:') ? parseInt(v.slice(2), 10) : null, ticketTypeId: v.startsWith('k:') ? parseInt(v.slice(2), 10) : null });
                }}>
          <option value="">— Just the event —</option>
          {tiers.length > 0 && <optgroup label="Sponsorship levels">{tiers.map(t => <option key={t.id} value={`t:${t.id}`}>{t.name}</option>)}</optgroup>}
          {types.length > 0 && <optgroup label="Ticket types">{types.map(t => <option key={t.id} value={`k:${t.id}`}>{t.name}</option>)}</optgroup>}
        </select>
      </div>
      <small style={{ display: 'block', color: '#888', marginTop: 6 }}>
        Tie this item to an event’s sponsorship level and every invoice for it adds the sponsor to that event (and shows on the company and contact).
        Tie it to a ticket type and paid invoices add the tickets.
      </small>
    </div>
  );
}

function ItemUsage({ itemId, onNavigate }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/invoice-items/${itemId}/usage`).then(r => setData(r.data)).catch(() => setData({ invoices: [], sponsors: [], tickets: [] })); }, [itemId]);
  if (!data) return <div style={{ fontSize: 13, color: '#888' }}>Loading where this item is used…</div>;
  const money = (n) => `$${parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  return (
    <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: '10px 12px', maxHeight: 260, overflow: 'auto' }}>
      <label className="form-label" style={{ marginBottom: 6 }}>Where it’s used</label>
      <div style={{ fontSize: 13, marginBottom: 6 }}>
        <strong>{data.invoices.length}</strong> invoice{data.invoices.length === 1 ? '' : 's'}
        {data.sponsors.length ? <> · <strong>{data.sponsors.length}</strong> sponsor{data.sponsors.length === 1 ? '' : 's'}</> : null}
        {data.tickets.length ? <> · <strong>{data.tickets.length}</strong> ticket{data.tickets.length === 1 ? '' : 's'}</> : null}
      </div>
      {data.invoices.length > 0 && (
        <table className="data-table" style={{ fontSize: 13 }}>
          <tbody>
            {data.invoices.map(i => (
              <tr key={i.id}>
                <td><Link to={`/invoices/${i.id}`} onClick={onNavigate} style={{ fontFamily: 'monospace' }}>{i.invoice_number}</Link></td>
                <td>{i.bill_to || ''}</td>
                <td>{i.issue_date}</td>
                <td style={{ textAlign: 'right' }}>{money(i.item_amount)}</td>
                <td><span className={`badge badge-${i.status}`}>{String(i.status).replace('_', ' ')}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data.sponsors.length > 0 && (
        <table className="data-table" style={{ fontSize: 13, marginTop: 8 }}>
          <tbody>
            {data.sponsors.map(sp => (
              <tr key={sp.id}>
                <td>{sp.sponsor_name}</td>
                <td>{sp.event_id ? <Link to={`/events/${sp.event_id}`} onClick={onNavigate}>{sp.event_name}</Link> : ''}</td>
                <td style={{ textAlign: 'right' }}>{money(sp.amount)}</td>
                <td><span className={`badge badge-${sp.payment_status}`}>{String(sp.payment_status).replace('_', ' ')}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
